import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  TableData,
  Pagination,
  ActionDropdown,
  ActionDropdownAction,
  InputSearch,
  Button,
  UniversalFilter,
  ColumnSettings,
} from '@/components';
import { useTableColumns } from '@/hooks/useTableColumns';
import { Column } from '@/types/common';
import {
  getBookingScheduleSettings,
  createBookingScheduleSetting,
  BookingScheduleSetting,
} from '@/services/api/settings';
import { getListPitstop, Pitstop } from '@/services/api/pitstops';
import { hubPositionFrom } from './hubPosition';
import {
  showErrorToast,
  showSuccessToast,
} from '@/components/toast/globalToast';
import { getUserEmail } from '@/utils';
import {
  CreateScheduleModal,
  ScheduleFormData,
} from './components/CreateScheduleModal';
import { ViewEditScheduleModal } from './components/ViewEditScheduleModal';
import { SettingsTabs } from './components/SettingsTabs';

interface SettingTableRow {
  id: string;
  hubLocation: string;
  activeDays: string[];
  timeWindows: string[];
  isActive: boolean;
  createdBy: string;
}

const DAY_LABELS: Record<number, string> = {
  0: 'Sun',
  1: 'Mon',
  2: 'Tue',
  3: 'Wed',
  4: 'Thu',
  5: 'Fri',
  6: 'Sat',
};

const formatTimeWindow = (startTime: string, endTime: string) =>
  `${startTime} - ${endTime}`;

const mapSettingToTableRow = (
  setting: BookingScheduleSetting
): SettingTableRow => ({
  id: setting.ID,
  hubLocation: setting.hubName || '-',
  activeDays: (setting.activeDays || []).map((day) => DAY_LABELS[day] || '-'),
  timeWindows: (setting.timeWindows || []).map((window) =>
    formatTimeWindow(window.startTime, window.endTime)
  ),
  isActive: Boolean(setting.isActive),
  createdBy: setting.createdBy || '-',
});

const SettingsPage: React.FC = () => {
  const [searchInput, setSearchInput] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [totalItems, setTotalItems] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [settingsData, setSettingsData] = useState<SettingTableRow[]>([]);
  const [rawSettings, setRawSettings] = useState<BookingScheduleSetting[]>([]);
  const [pitstops, setPitstops] = useState<Pitstop[]>([]);
  const [activeFilters, setActiveFilters] = useState<Record<string, any>>({});
  const [actionDropdownOpen, setActionDropdownOpen] = useState<
    string | number | null
  >(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isViewEditModalOpen, setIsViewEditModalOpen] = useState(false);
  const [selectedSetting, setSelectedSetting] =
    useState<BookingScheduleSetting | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [sortField, setSortField] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc' | null>(
    null
  );

  const fetchPitstops = useCallback(async () => {
    try {
      const response = await getListPitstop({
        size: '9999',
        type: 'HUB',
        hasStaff: true,
      });
      setPitstops(response.data || []);
    } catch (error) {
      console.error('Failed to fetch hubs:', error);
    }
  }, []);

  useEffect(() => {
    fetchPitstops();
  }, [fetchPitstops]);

  useEffect(() => {
    if (isCreateModalOpen) {
      fetchPitstops();
    }
  }, [isCreateModalOpen, fetchPitstops]);

  const fetchSettings = useCallback(
    async (
      page: number,
      size: number,
      search: string,
      filters: Record<string, any>
    ) => {
      setIsLoading(true);

      const isActiveFilter =
        filters?.isActive?.length === 1
          ? filters.isActive[0] === 'true'
          : undefined;

      try {
        const response = await getBookingScheduleSettings({
          page: String(page),
          size: String(size),
          order: 'desc',
          sort: 'createdAt',
          search: search || undefined,
          isActive: isActiveFilter,
          hubIDs: filters?.hubIDs?.length
            ? filters.hubIDs.join(',')
            : undefined,
          activeDays: filters?.activeDays?.length
            ? filters.activeDays.join(',')
            : undefined,
        });

        const apiData = response.data?.data || [];
        const pagination = response.data?.pagination || response.pagination;

        setRawSettings(apiData);
        setSettingsData(apiData.map(mapSettingToTableRow));
        setTotalItems(pagination?.total || 0);
      } catch (error) {
        console.error('Failed to fetch booking schedule settings:', error);
        setSettingsData([]);
        setTotalItems(0);
        showErrorToast(
          'Error',
          'Failed to load booking operational schedule settings'
        );
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    fetchSettings(currentPage, pageSize, searchTerm, activeFilters);
  }, [fetchSettings, currentPage, pageSize, searchTerm, activeFilters]);

  const filterConfigs = useMemo(
    () => [
      {
        key: 'hubIDs',
        title: 'Hub Location',
        icon: 'ri-map-pin-line',
        multiple: true,
        dataSource: pitstops,
        labelField: 'name',
        valueField: 'id',
      },
      {
        key: 'isActive',
        title: 'Status',
        icon: 'ri-filter-3-line',
        multiple: true,
        staticOptions: [
          { key: 'status_active', label: 'Active', value: 'true' },
          { key: 'status_inactive', label: 'Inactive', value: 'false' },
        ],
      },
      {
        key: 'activeDays',
        title: 'Active Days',
        icon: 'ri-calendar-line',
        multiple: true,
        staticOptions: [
          { key: 'day_1', label: 'Monday', value: '1' },
          { key: 'day_2', label: 'Tuesday', value: '2' },
          { key: 'day_3', label: 'Wednesday', value: '3' },
          { key: 'day_4', label: 'Thursday', value: '4' },
          { key: 'day_5', label: 'Friday', value: '5' },
          { key: 'day_6', label: 'Saturday', value: '6' },
          { key: 'day_0', label: 'Sunday', value: '0' },
        ],
      },
    ],
    [pitstops]
  );

  const handleEditAction = useCallback(
    (record: SettingTableRow) => {
      const raw = rawSettings.find((s) => s.ID === record.id) ?? null;
      setSelectedSetting(raw);
      setIsViewEditModalOpen(true);
      setActionDropdownOpen(null);
    },
    [rawSettings]
  );

  const handleCreateSubmit = async (data: ScheduleFormData) => {
    setIsSaving(true);
    try {
      const userEmail = getUserEmail();
      if (!userEmail)
        throw new Error('User email not found. Please log in again.');

      const selectedPitstop = pitstops.find((p) => p.id === data.hubID);
      if (!selectedPitstop) throw new Error('Selected hub not found.');

      await createBookingScheduleSetting({
        hubID: data.hubID,
        hubName: selectedPitstop.name,
        ...hubPositionFrom(selectedPitstop),
        activeDays: data.activeDays,
        timeWindows: data.timeWindows,
        isActive: data.isActive,
        createdBy: userEmail,
      });

      showSuccessToast('Success', 'Jadwal operasional berhasil dibuat');
      setIsCreateModalOpen(false);
      fetchSettings(1, pageSize, searchTerm, activeFilters);
    } catch (error) {
      console.error(error);
      const msg =
        error instanceof Error
          ? error.message
          : 'Gagal membuat jadwal. Coba lagi.';
      showErrorToast('Error', msg);
      throw error;
    } finally {
      setIsSaving(false);
    }
  };

  const handleSearch = useCallback((value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);
  }, []);

  const handleSort = (field: string, direction: 'asc' | 'desc' | null) => {
    setSortField(field);
    setSortDirection(direction);
    setCurrentPage(1);
  };

  const sortedData = useMemo(() => {
    if (!sortField || !sortDirection) return settingsData;

    return [...settingsData].sort((a: any, b: any) => {
      const aValue = a[sortField];
      const bValue = b[sortField];
      const comparison = String(aValue || '').localeCompare(
        String(bValue || '')
      );
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [settingsData, sortField, sortDirection]);

  const columns: Column<SettingTableRow>[] = [
    {
      title: 'Hub Location',
      key: 'hubLocation',
      width: 'w-48',
      sortable: true,
      render: (val: string) => (
        <span className="font-medium text-[14px] text-gray-900">{val}</span>
      ),
    },
    {
      title: 'Active Days',
      key: 'activeDays',
      width: 'w-64',
      render: (days: string[]) => (
        <div className="flex flex-wrap gap-1">
          {days.map((day) => (
            <span
              key={day}
              className="px-2.5 py-1 text-xs font-medium text-gray-600 bg-gray-100 rounded-full"
            >
              {day}
            </span>
          ))}
        </div>
      ),
    },
    {
      title: 'Time Windows',
      key: 'timeWindows',
      width: 'w-64',
      render: (timeWindows: string[]) => (
        <div className="flex flex-col gap-1">
          {timeWindows.length > 0 ? (
            timeWindows.map((timeWindow) => (
              <span key={timeWindow} className="text-[14px] text-gray-600">
                {timeWindow}
              </span>
            ))
          ) : (
            <span className="text-[14px] text-gray-600">-</span>
          )}
        </div>
      ),
    },
    {
      title: 'Status',
      key: 'isActive',
      width: 'w-32',
      sortable: true,
      render: (isActive: boolean) => (
        <span
          className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
            isActive ? 'bg-[#1BB55C] text-white' : 'bg-gray-500 text-white'
          }`}
        >
          {isActive ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    {
      title: 'Created By',
      key: 'createdBy',
      sortable: true,
      render: (val: string) => (
        <span className="text-[14px] text-gray-500">{val}</span>
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 'w-16',
      align: 'right',
      render: (_: any, record: SettingTableRow) => {
        const rowActions: ActionDropdownAction[] = [
          {
            key: 'edit',
            label: 'Edit',
            icon: 'ri-pencil-line',
            onClick: () => handleEditAction(record),
          },
        ];
        return (
          <div
            className="flex justify-end"
            onClick={(e) => e.stopPropagation()}
          >
            <ActionDropdown
              record={record}
              actions={rowActions}
              isOpen={actionDropdownOpen === record.id}
              onToggle={() =>
                setActionDropdownOpen(
                  actionDropdownOpen === record.id ? null : record.id
                )
              }
            />
          </div>
        );
      },
    },
  ];

  // Column customization (show/hide + reorder), persisted per browser.
  const { visibleColumns, columnSettingsProps } =
    useTableColumns<SettingTableRow>({
      storageKey: 'settings',
      columns,
    });

  return (
    <div className="bg-white p-6 max-w-full mx-auto">
      <div className="mb-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-[24px] font-semibold text-gray-900 mb-1">
              Settings
            </h1>
            <p className="text-[14px] text-gray-600">
              Manage vehicle pickup schedules across all regional hubs.
            </p>
          </div>
          <Button
            variant="primary"
            size="small"
            className="whitespace-nowrap flex-shrink-0"
            onClick={() => setIsCreateModalOpen(true)}
          >
            <i className="ri-add-line text-[12px] mr-1"></i> Create Schedule
          </Button>
        </div>
      </div>
      <SettingsTabs active="booking-schedule" />

      <div className="rounded-lg p-2 mb-4">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center"></div>

          <div className="flex items-center gap-2">
            <InputSearch
              placeholder="Search…"
              value={searchInput}
              onChange={setSearchInput}
              onSearch={handleSearch}
              onClear={() => {
                setSearchInput('');
                handleSearch('');
              }}
              debounceMs={300}
              size="small"
            />

            <UniversalFilter
              filters={filterConfigs}
              currentFilters={activeFilters}
              onApply={(filters) => {
                setActiveFilters(filters);
                setCurrentPage(1);
              }}
              onClear={() => {
                setActiveFilters({});
                setCurrentPage(1);
              }}
            />

            <ColumnSettings {...columnSettingsProps} />
          </div>
        </div>
      </div>

      {searchTerm && !isLoading && (
        <div className="mb-3 px-2">
          <p className="text-sm text-gray-600">
            Found <span className="font-semibold">{totalItems}</span> result
            {totalItems !== 1 ? 's' : ''} for{' '}
            <span className="font-semibold">'{searchTerm}'</span>
          </p>
        </div>
      )}

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="relative">
          <TableData
            data={sortedData}
            columns={visibleColumns}
            loading={isLoading}
            sorting={{
              field: sortField,
              direction: sortDirection,
              onSort: handleSort,
            }}
            size="small"
            className="min-w-full"
          />
        </div>
      </div>

      <Pagination
        currentPage={currentPage}
        totalItems={totalItems}
        itemsPerPage={pageSize}
        onPageChange={setCurrentPage}
        onPageSizeChange={(newSize) => {
          setPageSize(newSize);
          setCurrentPage(1);
        }}
      />

      <CreateScheduleModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSubmit={handleCreateSubmit}
        isLoading={isSaving}
        pitstops={pitstops}
      />

      <ViewEditScheduleModal
        isOpen={isViewEditModalOpen}
        onClose={() => {
          setIsViewEditModalOpen(false);
          setSelectedSetting(null);
        }}
        setting={selectedSetting}
        pitstops={pitstops}
        onUpdated={() => {
          setIsViewEditModalOpen(false);
          setSelectedSetting(null);
          fetchSettings(currentPage, pageSize, searchTerm, activeFilters);
        }}
      />
    </div>
  );
};

export default SettingsPage;
